/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, ReactNode, useState } from "react"
import Bowser from "bowser"
import { CopyIcon } from "@medaica/common/views/compatibility-test/icons/CopyIcon"
import { ActionButton, Card, HelpBand } from "components/help-page"
import { Paths } from "const"

const chromeDownloadUrl = "https://www.google.com/chrome/"

const Step = ({ children }: { children: ReactNode }): ReactElement => <li className="pl-1">{children}</li>

/** Installing Chrome, step by step, on the computers patients use. */
const installSteps: Record<string, { steps: ReactElement[]; requirement: string }> = {
  macOS: {
    steps: [
      <Step key="open">
        Open the file that downloads, <strong>googlechrome.dmg</strong>.
      </Step>,
      <Step key="drag">
        Drag the Chrome icon into the <strong>Applications</strong> folder.
      </Step>,
      <Step key="password">If your Mac asks, enter your Mac&apos;s password.</Step>,
    ],
    requirement: "Chrome needs macOS 13 Ventura or later.",
  },
  Windows: {
    steps: [
      <Step key="open">
        Open the file that downloads, <strong>ChromeSetup.exe</strong>.
      </Step>,
      <Step key="allow">
        When Windows asks <em>“Do you want to allow this app to make changes to your device?”</em>, choose{" "}
        <strong>Yes</strong>.
      </Step>,
      <Step key="opens">Chrome opens by itself when it&apos;s ready.</Step>,
    ],
    requirement: "Chrome needs Windows 10 or later.",
  },
}

const CopyLink = ({ url }: { url: string }): ReactElement => {
  const [copied, setCopied] = useState(false)

  const copy = () => {
    void navigator.clipboard.writeText(url).then(() => setCopied(true))
  }

  return (
    <div className="flex max-w-xl items-center rounded border border-primary-200 bg-primary-100">
      <span className="flex-1 truncate px-3 py-2 text-gray-800" data-testid="exam-link">
        {url}
      </span>
      <button
        type="button"
        onClick={copy}
        className="flex items-center gap-2 border-l border-primary-200 px-4 py-2 text-primary font-semibold"
      >
        <CopyIcon />
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  )
}

/** Where to type the access code, as an address bar would show it. */
const AddressPill = ({ address }: { address: string }): ReactElement => (
  <div
    className={
      "inline-flex items-center gap-3 rounded-full border border-gray-300 bg-gray-50 px-5 py-2 text-lg text-gray-900"
    }
  >
    <span className="text-gray-400" aria-hidden="true">
      &#128274;
    </span>
    {address}
  </div>
)

const GetChromeButton = ({ variant }: { variant: "primary" | "secondary" }): ReactElement => (
  <ActionButton
    href={chromeDownloadUrl}
    variant={variant}
    title="Get Google Chrome"
    subtitle="Free, from google.com/chrome"
  />
)

/** The Chrome install steps, lined up with a card's content: under a numbered title (`step`) or a plain one. */
const InstallSteps = ({ osName, step }: { osName: string; step: boolean }): ReactElement | null => {
  const install = installSteps[osName]
  if (!install) {
    return null
  }
  return (
    <>
      <ol className={`list-decimal mt-6 space-y-2 text-gray-700 ${step ? "ml-14" : "ml-9"}`}>{install.steps}</ol>
      <p className={`mt-4 text-sm text-gray-500 ${step ? "ml-10" : "ml-5"}`}>{install.requirement}</p>
    </>
  )
}

/** Windows comes with Microsoft Edge, which runs the exam, so the patient is pointed there before Chrome. */
const WindowsHelp = ({ codeEntryPage }: { codeEntryPage: string }): ReactElement => (
  <>
    <Card title="Use Microsoft Edge" lead="Edge comes with Windows, so it's already on this computer.">
      <div className="ml-5 max-w-sm">
        <ActionButton
          href={`microsoft-edge:${window.location.href}`}
          newWindow={false}
          variant="primary"
          title="Open in Microsoft Edge"
          subtitle="Your exam opens in Edge"
        />
      </div>
      <p className="ml-5 mt-6 mb-4 text-gray-700">
        If Edge doesn&apos;t open, open it from the Start menu, go to{" "}
        <strong className="text-gray-900">{codeEntryPage}</strong> and enter the access code from your email.
      </p>
      <div className="ml-5">
        <AddressPill address={codeEntryPage} />
        <p className="mt-8 mb-2 text-gray-700">Or copy this link and paste it into Edge:</p>
        <CopyLink url={window.location.href} />
      </div>
    </Card>

    <Card title="Or use Google Chrome" lead="Install Chrome, then open your exam in Chrome the same way.">
      <div className="ml-5 max-w-sm">
        <GetChromeButton variant="secondary" />
      </div>
      <InstallSteps osName="Windows" step={false} />
    </Card>
  </>
)

const ChromeHelp = ({ osName, codeEntryPage }: { osName: string; codeEntryPage: string }): ReactElement => (
  <>
    <Card title="Get Google Chrome" lead="Skip this step if Chrome is already on this computer." step={1}>
      <div className="ml-10 max-w-sm">
        <GetChromeButton variant="primary" />
      </div>
      <InstallSteps osName={osName} step={true} />
    </Card>

    <Card
      title="Open your exam in Chrome"
      lead={
        <>
          Open Chrome, go to <strong className="text-gray-900">{codeEntryPage}</strong> and enter the access code from
          your email.
        </>
      }
      step={2}
    >
      <div className="ml-10">
        <AddressPill address={codeEntryPage} />
        <p className="mt-8 mb-2 text-gray-700">Or copy this link and paste it into Chrome:</p>
        <CopyLink url={window.location.href} />
      </div>
    </Card>
  </>
)

/**
 * Shown on /live-exam/<id> in a computer browser that can't run the exam, which is any browser other than Chrome
 * or Edge: helps the patient open the same exam in a browser that can, either through the access code in their
 * email or by copying this page's link. On Windows that is Microsoft Edge, which comes with Windows, with Chrome as
 * the alternative; elsewhere it is Google Chrome. The page stays at the exam's URL so the link can be copied.
 */
const UnsupportedBrowserPage = ({ userAgent }: { userAgent: string }): ReactElement => {
  const parser = Bowser.getParser(userAgent)
  const browserName = parser.getBrowserName()
  const osName = parser.getOSName()
  const onWindows = osName === "Windows"
  const codeEntryPage = `${window.location.host}${Paths.liveExam}`
  const openedIn = browserName ? `This link opened in ${browserName}. ` : ""

  return (
    <div className="max-w-3xl mx-auto pb-8 px-4">
      <div className="text-center py-10">
        <h1 className="text-4xl font-extrabold tracking-tight">
          {onWindows ? "Open your exam in Microsoft Edge" : "Your exam needs Google Chrome on this computer"}
        </h1>
        <p className="text-lg text-gray-500 mt-3">
          {openedIn}
          {onWindows ? "Your exam runs in Microsoft Edge or Google Chrome." : "Your exam runs in Google Chrome."}
        </p>
      </div>

      {onWindows ? (
        <WindowsHelp codeEntryPage={codeEntryPage} />
      ) : (
        <ChromeHelp osName={osName} codeEntryPage={codeEntryPage} />
      )}

      <HelpBand heading="Need help? Call us" />
    </div>
  )
}

export default UnsupportedBrowserPage
